import type { Sanitizer } from '@angular/core';

import { pipe } from 'rxjs';

import {
  DatasetBase,
  MAX_SECOND_DIMENSION_LENGTH,
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewData,
  ReportDashboardComponentViewMode,
  ReportDashboardMetricOptions,
  ReportsBarChartOptions,
  ReportsEChartsOption,
  ReportsLineChartOptions,
  VisualizationMapperOperator,
  DataInterval,
  ReportsTranslations,
  Blueprint,
} from '@core/reports-types';
import { datasetChartLabelFormatterOptions } from '@core/reports-util';
import { pipeCompose } from '@core/utils-legacy';

import { pivotTable, summary, table } from './generic';
import { addIntervalXAxis, addTrendSeries, addValueYAxis } from './strategies/composables/trend';
import { getXAxisNameLabel } from './strategies/get-x-axis-name-label';
import { filterByMetricKind, fromBlueprints } from './strategies/operators';
import { trendTooltipContextFactory } from './strategies/trend/tooltip';

type TrendBarChartOpts = ReportsBarChartOptions<'time' | 'category', 'value'>;
type TrendLineChartOpts = ReportsLineChartOptions<'time' | 'category', 'value'>;

const barChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
  clampEndToToday: boolean,
): TrendBarChartOpts => {
  const stack = component.primaryGroup !== null ? dataset.getIndexDimension()?.id : null;

  const opts = () => ({
    legend: {
      formatter: (id: string) => dataset.getDimensionNameById(id),
    },
    tooltip: <any>{
      show: true,
      trigger: <const>'axis',
      axisPointer: {
        type: <const>'none',
      },
      formatter: (params: any) =>
        trendTooltipContextFactory(
          {
            dateFilter: component.dateFilter,
            interval: <DataInterval>component.settings?.interval!,
            clampEndToToday,
          },
          dataset,
          translations,
          params,
        ),
    },
    dataset: dataset.dataset,
    series: { type: <const>'bar' },
    graphic: [getXAxisNameLabel(metric.xAxis)],
    stack,
  });

  return pipeCompose(
    opts,
    addTrendSeries<'bar'>(dataset),
    addValueYAxis(datasetChartLabelFormatterOptions(dataset), translations.i18n),
    addIntervalXAxis(dataset, <DataInterval>component.settings?.interval),
  ) satisfies ReportsEChartsOption;
};

const lineChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
  clampEndToToday: boolean,
): TrendLineChartOpts => {
  const opts = () => ({
    graphic: [getXAxisNameLabel(metric.xAxis)],
    legend: {
      formatter: (id: string) => dataset.getDimensionNameById(id),
    },
    tooltip: <any>{
      show: true,
      trigger: <const>'axis',
      axisPointer: {
        type: <const>'line',
      },
      formatter: (params: any) =>
        trendTooltipContextFactory(
          {
            dateFilter: component.dateFilter,
            interval: <DataInterval>component.settings?.interval!,
            clampEndToToday,
          },
          dataset,
          translations,
          params,
        ),
    },
    dataset: dataset.dataset,
    series: {
      type: <const>'line',
    },
  });

  return pipeCompose(
    opts,
    addTrendSeries<'line'>(dataset),
    addValueYAxis(datasetChartLabelFormatterOptions(dataset), translations.i18n),
    addIntervalXAxis(dataset, <DataInterval>component.settings?.interval),
  ) satisfies ReportsEChartsOption;
};

export const trend = (
  sanitizer: Sanitizer,
  translations: ReportsTranslations,
  clampEndToToday = false,
): VisualizationMapperOperator =>
  pipe(
    filterByMetricKind(ReportDashboardComponentKind.Trend),
    fromBlueprints(trendBlueprints(sanitizer, translations, clampEndToToday)),
  );

export const trendBlueprints = (
  sanitizer: Sanitizer,
  translations: ReportsTranslations,
  clampEndToToday = false,
): Array<Blueprint<any>> => [
  table(translations.i18n, ReportDashboardComponentKind.Trend),
  pivotTable(translations, ReportDashboardComponentKind.Trend),
  summary(translations.i18n, ReportDashboardComponentKind.Trend),
  {
    match: {
      kind: ReportDashboardComponentKind.Trend,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Column,
    },
    factory: ({ component, dataset, metric, meta }) => {
      let newDataset = dataset;
      if (newDataset.dimensions.length > 3) {
        newDataset = dataset.truncateDataset(
          'second',
          MAX_SECOND_DIMENSION_LENGTH,
          true,
          translations.i18n.translate('tlr.group_name.other'),
        );
      }
      return {
        vis: barChart(component, newDataset, metric, translations, clampEndToToday),
        dataset: newDataset,
      };
    },
  },
  {
    match: {
      kind: ReportDashboardComponentKind.Trend,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Line,
    },
    factory: ({ component, dataset, metric, meta }) => {
      let newDataset = dataset;
      if (newDataset.dimensions.length > 3) {
        newDataset = dataset.truncateDataset(
          'second',
          MAX_SECOND_DIMENSION_LENGTH,
          true,
          translations.i18n.translate('tlr.group_name.other'),
        );
      }
      return {
        vis: lineChart(component, newDataset, metric, translations, clampEndToToday),
        dataset: newDataset,
      };
    },
  },
];
